/**
 * Connect AI-voice decision vocabulary: the closed value sets the voice worker, its two Durable Objects and the
 * `calls` row all read from, so the DO, the webhook entry and the flow leaves share ONE spelling of every literal that
 * also appears in migration SQL or on a Twilio wire.
 *
 * ONE VALUE IMPORT, `TIME_UNITS`, WHICH IS THE ZERO-IMPORT TIME LEAF. It is here so a saturation expressed in
 * milliseconds against a cap expressed in seconds converts through the fleet's own unit record rather than a literal
 * `1000`; the leaf it comes from imports nothing at all, so no cycle is reachable through it.
 *
 * THE CAPS ARE ENFORCEMENT, NOT DOCUMENTATION. `VOICE_CAPS` is read inside `ConnectCallDO` on every turn and every
 * relay frame; a cap that fires is recorded on the calls row's `caps_fired` JSON and ends the AI leg through
 * `{type:"end", handoffData}`, so changing a number here changes live call behaviour rather than a comment.
 *
 * THE RISK SCALAR IS DETERMINISTIC AND MODEL-FREE. `VOICE_SIGNAL_WEIGHTS` sums to `VOICE_RISK_MAX` by construction
 * (pinned by the sibling suite), and each signal is normalized against its `VOICE_SIGNAL_SATURATION` entry before it
 * is weighted, so the scalar stays inside `0..VOICE_RISK_MAX` whatever a counter reaches. Nothing here consults a
 * model: the escalate-on-"agent" path and the DTMF-0 path must answer in microseconds and must not be
 * talkable-out-of.
 */

import { TIME_UNITS } from '@federa/const/time-units';
// Type-only, so this stays the leaf's ONLY value import: the erased import cannot close a module cycle with the types
// leaf that reads this file's own vocabularies back.
import type { VoiceCallDirection, VoiceCallStatus, VoiceRouteVerdict } from '@federa/types/connect-voice-control';
import type { FlowInputSchema } from '@federa/types/flow-engine-input-schema';

/** `calls.status` domain. Mirrors the CHECK list in `migrations/federa-db/0004_connect_voice.sql` exactly, and BINDS to
 *  `VoiceCallStatus` so the mirror is checked rather than asserted: a dropped, misspelled or CHECK-orphaned member is a
 *  compile error here instead of a SQLITE_CONSTRAINT_CHECK on the next write. Same clause, same reason, as
 *  `VOICE_ROUTE_VERDICTS` below. */
export const VOICE_CALL_STATUS = {
	IN_PROGRESS: 'in_progress',
	COMPLETED: 'completed',
	FAILED: 'failed',
	VOICEMAIL: 'voicemail',
	TAKEN_OVER: 'taken_over',
} as const satisfies Record<string, VoiceCallStatus>;

/** `calls.direction` domain. Mirrors the CHECK list in `migrations/federa-db/0004_connect_voice.sql` and BINDS to
 *  `VoiceCallDirection` so the mirror is checked rather than asserted -- the same clause and the same reason as
 *  `VOICE_CALL_STATUS` above and `VOICE_ROUTE_VERDICTS` below, which is what makes this leaf's binding coverage
 *  complete: until 2026-08-23 this was the one vocabulary here checked against nothing, while `CallRow.direction`
 *  (`types/d1-calls.ts`) spelled the same pair inline. */
export const VOICE_CALL_DIRECTIONS = {
	INBOUND: 'inbound',
	OUTBOUND: 'outbound',
} as const satisfies Record<string, VoiceCallDirection>;

// `calls.identity_confidence` is NOT spelled here: the column's value domain is owned by
// `const/connect-voice-control.ts` beside the OTP actions that write it (VOICE_IDENTITY_CONFIDENCE).

/**
 * Closed reason codes on the `handoffData` JSON the DO sends with `{type:"end"}`. Twilio then POSTs the
 * `<Connect action>` URL and OUR TwiML answers voicemail or a conference -- the reason is what that handler branches
 * on, so an unlisted string would silently take the default (voicemail) arm.
 *
 * THREE MEMBERS ALSO SERVE AN ANSWER-TIME ROLE, and it is a DIFFERENT one: `KILL_SWITCH`, `CAP_REACHED` and
 * `ASSISTANT_UNAVAILABLE` are stamped on `calls.handoff_reason` by the ANSWER path, at INSERT, for a call the router
 * sent straight to voicemail without ever minting a relay socket. Such a row never reaches the `<Connect action>`
 * handler at all -- there is no AI leg to end, so Twilio never fetches that URL -- which is why an answer-time-only
 * member is safe to add here without an arm in `shapeVoiceActionTwiml`. The row is the ONLY place the denial cause is
 * recorded; without it the deck rendered a denied call as a live one with no explanation anywhere.
 */
export const VOICE_HANDOFF_REASONS = {
	ASSISTANT_TIMEOUT: 'assistant_timeout',
	ESCALATE_TO_HUMAN: 'escalate_to_human',
	SUPERVISOR_TAKEOVER: 'supervisor_takeover',
	/**
	 * The operator ENDED the call from the desk -- the Hang up control, not a handoff to anyone.
	 *
	 * IT IS ITS OWN MEMBER BECAUSE THE HANGUP ARM USED {@link VOICE_HANDOFF_REASONS.SUPERVISOR_TAKEOVER}, AND THE
	 * `<Connect action>` HANDLER BRANCHES ON EXACTLY THAT STRING. So a hang-up answered `<Say>a member of our team is
	 * joining</Say><Dial><Conference>` -- it BRIDGED the caller into an empty per-call conference instead of ending the
	 * leg, and stamped `disposition` and `handoff_reason` as a takeover nobody had performed. Two different acts sharing
	 * one reason code is exactly the collapse this member was split out to end.
	 *
	 * Its arm in `shapeVoiceActionTwiml` answers a bare `<Hangup/>`: the operator's gesture was "end the call now", and
	 * any spoken line would keep the caller on a leg that was already ended.
	 */
	SUPERVISOR_HANGUP: 'supervisor_hangup',
	CALLER_REQUESTED: 'caller_requested',
	CAP_REACHED: 'cap_reached',
	KILL_SWITCH: 'kill_switch',
	/** The remote end closed the relay socket -- the caller hung up (or the carrier dropped the leg). Recorded so a
	 *  leg that ends this way still finalizes: `webSocketClose` routes it through `endCall`, which is what clears the
	 *  escalation deadline and decrements the fleet's live-call count. `handoff_reason` carries no CHECK (0004). */
	CALLER_HANGUP: 'caller_hangup',
	/** The caller went quiet mid-conversation, was checked in on once, and stayed quiet (operator 2026-09-30). Its
	 *  `shapeVoiceActionTwiml` arm says goodbye and hangs up: a caller who was talking a minute ago is not a mailbox. */
	CALLER_SILENT: 'caller_silent',
	// NO `RECORDING_DECLINED` MEMBER ANY MORE. The press-nine decline-recording path existed solely to honour the
	// spoken consent notice's own offer, and the notice was REMOVED by operator adjudication 2026-08-21 (recording
	// continues; the operator accepts the exposure). With nothing advertising the digit, the whole chain -- the DTMF
	// arm, the unrecorded-mailbox TwiML arm, and the archive flow's consent gate -- was deleted in the same change.
	// Historical rows may still carry the literal `recording_declined`; readers treat it like any other unlisted string
	// (the voicemail default arm), which is the behaviour an unlisted reason always had.
	/**
	 * TWILIO SAID THE RELAY SESSION FAILED. Written by the `<Connect action>` close-out when the callback carries
	 * `SessionStatus=failed` ({@link https://www.twilio.com/docs/voice/conversationrelay/conversationrelay-noun}, whose
	 * failure example is `ErrorCode` "39001" / "Network connection to WebSocket server failed.").
	 *
	 * IT IS THE ONLY THING THAT DISTINGUISHES A FAILED SESSION FROM A HEALTHY CALL, and until 2026-08-15 nothing did. A
	 * Twilio-side failure means the DO sent no `end` frame, so the callback carries NO `HandoffData` -- the close-out
	 * therefore read an empty reason, the close-out status decision fell through to `completed` (the standard params say
	 * `CallStatus=in-progress`, which is not in `TWILIO_FAILED_CALL_STATUSES`), and `UPDATE_CONNECT_CALL_END_SQL`'s
	 * `COALESCE(NULLIF(?, ''), handoff_reason)` left the reason NULL. The row a failed relay produced was byte-identical
	 * to the row a healthy call produced, which is why eight rounds of audit could not localise a failing call.
	 *
	 * IT NEEDS NO ARM IN `shapeVoiceActionTwiml` and deliberately has none: the caller whose AI leg just died gets the
	 * default RECORDED mailbox, which is the right outcome, and `logActionDefaultArm` names the reason on the way there.
	 *
	 * IT IS THE ONE MEMBER `voice-call-close-out` MAPS TO `failed` RATHER THAN `voicemail`, and the precedent is
	 * `ConnectCallDO.closeUnverifiedRelaySocket`, which already records a mid-call relay-handshake failure as
	 * `VOICE_CALL_STATUS.FAILED`. `failed` on this row does not mean "the carrier never connected the call" -- that
	 * reading belongs to `TWILIO_FAILED_CALL_STATUSES` -- it means "this call did not do what it was answered to do".
	 */
	RELAY_SESSION_FAILED: 'relay_session_failed',
	/**
	 * ANSWER-TIME ONLY: the assistant was not offered the call because the fleet said it could not take one -- the
	 * ConnectInbox circuit breaker is tripped, or the singleton itself was unreachable or unparsable.
	 *
	 * It is its OWN member rather than a flavour of {@link VOICE_HANDOFF_REASONS.CAP_REACHED} because the two denials
	 * need DIFFERENT operator actions: a ceiling clears itself as calls end, while a HOLDING breaker clears only when
	 * `VOICE_CIRCUIT_BREAKER_COOLDOWN_MS` has passed since the last failure and the probe call it then admits SUCCEEDS
	 * -- or when a human POSTs `VOICE_INBOX_ROUTES.RESET_ADMISSION`, which is the only way back that costs no caller.
	 * Collapsing them would have hidden the 2026-08-14 incident inside the ordinary overflow reason.
	 *
	 * NO ARM IS OWED IN `shapeVoiceActionTwiml`. This value is written at INSERT on a call that never got a relay
	 * socket, so it never rides `handoffData` and the `<Connect action>` handler never sees it.
	 */
	ASSISTANT_UNAVAILABLE: 'assistant_unavailable',
} as const;

/** Every cap the DO enforces. Each firing is appended to `calls.caps_fired`, so cap pressure is measurable rather than anecdotal. */
export const VOICE_CAPS = {
	/** Wall-clock ceiling on one AI leg. */
	MAX_CALL_SECONDS: 900,
	/** Caller utterances answered before the leg hands off. */
	MAX_TURNS: 40,
	// NO `MAX_TOKENS_PER_CALL`. The `/chunk` body carries no `tokens` member -- `makeVoiceChunkForwarder`'s body literal
	// is closed (`{ callSid, seq, text, last }`) and `VoiceChunkPost` declares no such member -- so a per-call budget
	// summed in the chunk sink would be a constant zero: a ceiling that never fires, which the operator would believe
	// exists. ADDING ONE NEEDS A USAGE SOURCE FIRST: the SDK stream tap
	// (`utils/assistant/agent-run-voice-stream-tap.ts`) reads text deltas only and extracts no usage event, so a real
	// budget needs a usage extractor on the completion event, threaded through `VoiceChunkSink.finish` onto the final
	// chunk body -- not a const here and a `+=` at the sink.
	/** Fleet ceiling read off ConnectInboxDO at answer time; overflow answers voicemail instead of engaging the AI. */
	MAX_CONCURRENT_AI_CALLS: 10,
	/** Per-ANI answer-rate ceiling: an unauthenticated caller minting provisioning work off a spoofable number. */
	PER_ANI_CALLS_PER_HOUR: 6,
	/** Silence after the AI finished speaking before the leg is treated as dead air. */
	DEAD_AIR_TIMEOUT_MS: 12000,
	/** The extra quiet a caller mid-conversation is given after the one check-in line, before the call ends politely
	 *  (`call-handlers/call-silence.ts`, operator 2026-09-30). */
	DEAD_AIR_CHECK_IN_GRACE_MS: 45000,
	/** How long the line is held after an approval link is sent to the caller's phone, so they can open and approve it. */
	APPROVAL_LINK_HOLD_MS: 120000,
	/** The longest hold a `hold_line` control may ask for -- the wall clock still bounds the whole leg. */
	HOLD_LINE_MAX_MS: 300000,
	/** The relay socket must present a valid `setup` frame inside this window or the DO closes it 1008. */
	RELAY_SETUP_DEADLINE_MS: 5000,
	/** Sentence-chunk flush ceiling on the assistant push path -- a run-on clause still reaches TTS promptly. */
	CHUNK_FLUSH_MAX_CHARS: 160,
	/**
	 * How long one escalation rung has to acknowledge before the ladder advances. It is a CAP rather than a timer
	 * setting because it bounds how long a distressed caller waits on an unattended AI leg -- the ladder's whole purpose
	 * -- and because the DO enforces it the same way it enforces every other cap.
	 */
	ESCALATION_ACK_TIMEOUT_MS: 45000,
	/**
	 * How long a caller holds for a human who has ACKNOWLEDGED their escalation but has not taken the call over yet.
	 *
	 * IT IS A SECOND CAP RATHER THAN A SECOND USE OF THE ONE ABOVE BECAUSE THE TWO MEASURE DIFFERENT EVIDENCE. The ack
	 * window bounds a page NOBODY has answered -- Twilio accepting a create is the whole of what the fleet observes -- so
	 * it is short and the ladder walks past it. This window opens on the one signal in the system that a specific person
	 * has CLAIMED this call, and it is longer because that person still has to open the deck and press Take over. It is
	 * bounded all the same: an acknowledgement that produces no takeover returns the caller to the ladder rather than
	 * holding them on a promise, and the wall clock bounds the leg however many times that repeats.
	 *
	 * WITHOUT IT THE ACK ARMED A 12-SECOND FUSE. Acknowledging cleared the escalation deadline outright, and a zero
	 * deadline is the entire dead-air suppression -- so the caller who had just been claimed by a human went back on the
	 * abandonment ceiling while sitting silent by design, and `DEAD_AIR_TIMEOUT_MS` dropped them into a mailbox twelve
	 * seconds later, on the desk's own designed path (ack, failed takeover, retry).
	 */
	ESCALATION_HOLD_TIMEOUT_MS: 90000,
} as const;

/**
 * The entry names a fired cap is recorded under in `calls.caps_fired` -- one spelling for `evaluateVoiceCaps`, which
 * pushes them, and for `ConnectCallDO`, which branches on one of them.
 *
 * IT IS A SEPARATE RECORD FROM {@link VOICE_CAPS} because the two have different membership: `VOICE_CAPS` also carries
 * the admission ceilings and the handshake/flush budgets, none of which are ever "fired" onto a row. Deriving these
 * from the cap record's keys would therefore promise entries no evaluator can produce.
 */
export const VOICE_CAP_KEYS = {
	MAX_CALL_SECONDS: 'MAX_CALL_SECONDS',
	MAX_TURNS: 'MAX_TURNS',
	DEAD_AIR_TIMEOUT_MS: 'DEAD_AIR_TIMEOUT_MS',
} as const;

/**
 * Deterministic distress signals, all computed inside the DO from frame bookkeeping. Keys are the `calls.signals` JSON
 * member names.
 *
 * EVERY MEMBER HAS A WRITER, AND THAT IS A MEMBERSHIP RULE RATHER THAN AN OBSERVATION. A member with NO producer
 * reads zero on every call, lowers the achievable ceiling below the declared 100, and tells an operator reading the
 * deck's legend that the desk watches something it cannot see -- which is why there is no `repeat_caller` member.
 * Adding it needs a WRITER first: the answer path's ANI count (`voice-call-answer`'s `count-ani` step) is AI-HANDLED
 * calls only (`COUNT_CONNECT_AI_CALLS_BY_ANI_SQL`, whose predicate the rate cap needs), it lives on the answer graph
 * rather than on the DO checkpoint the signals record belongs to, and a repeat-caller reading wants ALL prior calls --
 * so the missing half is a statement, a seeding channel to the DO, and only then a row here.
 */
export const VOICE_SIGNALS = {
	INTERRUPT_COUNT: 'interrupt_count',
	DEAD_AIR_MS: 'dead_air_ms',
	/**
	 * How long THE CALL has been running, in ms -- NOT one turn.
	 *
	 * IT WAS NAMED `turn_elapsed_ms` AND MEASURED THE CALL, which made it a constant floor rather than a signal. The
	 * producer sets `now - checkpoint.startedAt`, `startedAt` is the CALL's start, and the checkpoint carries no
	 * per-turn stamp to measure a turn from -- so against the old 20-second saturation every call past 20 seconds
	 * carried the signal's full weight forever, identically, and it could never distinguish one call from another. The
	 * name moved to the producer rather than the other way round: the desk copy already described it as call pacing,
	 * so two of the three positions agreed and only the name and its threshold did not.
	 */
	CALL_ELAPSED_MS: 'call_elapsed_ms',
	AI_REPEAT: 'ai_repeat',
	CALLER_REPEAT: 'caller_repeat',
	DTMF_ZERO: 'dtmf_zero',
	TOOL_FAILURE: 'tool_failure',
} as const;

/** Weighted contribution of each signal to the risk scalar. Sums to {@link VOICE_RISK_MAX}; the sibling suite pins that. */
export const VOICE_SIGNAL_WEIGHTS = {
	[VOICE_SIGNALS.INTERRUPT_COUNT]: 18,
	[VOICE_SIGNALS.DEAD_AIR_MS]: 12,
	[VOICE_SIGNALS.CALL_ELAPSED_MS]: 10,
	[VOICE_SIGNALS.AI_REPEAT]: 15,
	[VOICE_SIGNALS.CALLER_REPEAT]: 12,
	[VOICE_SIGNALS.DTMF_ZERO]: 20,
	[VOICE_SIGNALS.TOOL_FAILURE]: 8,
} as const;

/** Value at which a signal contributes its FULL weight; anything above saturates rather than overflowing the scalar. */
export const VOICE_SIGNAL_SATURATION = {
	[VOICE_SIGNALS.INTERRUPT_COUNT]: 3,
	[VOICE_SIGNALS.DEAD_AIR_MS]: VOICE_CAPS.DEAD_AIR_TIMEOUT_MS,
	// THE WALL-CLOCK CEILING, because that is the only threshold a CALL-elapsed reading can mean anything against: the
	// signal now reads "how far this call is through the longest life the DO will grant it", ramping from nothing at
	// answer to full weight at the moment `MAX_CALL_SECONDS` ends the leg. The 20-second value it replaces belonged to
	// the per-TURN reading the name used to promise, and against a call clock it saturated on every call.
	[VOICE_SIGNALS.CALL_ELAPSED_MS]: VOICE_CAPS.MAX_CALL_SECONDS * TIME_UNITS.SECOND_MS,
	[VOICE_SIGNALS.AI_REPEAT]: 2,
	[VOICE_SIGNALS.CALLER_REPEAT]: 2,
	[VOICE_SIGNALS.DTMF_ZERO]: 1,
	[VOICE_SIGNALS.TOOL_FAILURE]: 2,
} as const;

/**
 * Ceiling of the risk scalar; also the exact sum of {@link VOICE_SIGNAL_WEIGHTS}, which the sibling suite pins.
 *
 * IT IS 95 AND NOT A ROUND 100 BECAUSE THE WEIGHTS ARE THE TUNING AND THIS IS THEIR SUM. Deleting the writer-less
 * `repeat_caller` signal took its weight of 5 out with it; re-inflating the remaining seven to land back on 100 would
 * have re-tuned six live signals to preserve a round number, and the number is not the decision -- the relative
 * contributions are. The scalar is an ORDERING key (`ORDER BY risk DESC`) and never rendered as a figure on the deck,
 * so the range mattering here is that the declared ceiling is the ACHIEVABLE one, which it now is.
 */
export const VOICE_RISK_MAX = 95;

/** Word-set overlap at or above this ratio counts as a repeated utterance. Tuned to catch a re-phrased restatement while letting a genuine short follow-up through. */
export const VOICE_REPEAT_OVERLAP_THRESHOLD = 0.7;

/** The estimate's rate, and the worst rate a real voice is assumed to reach. Both feed {@link VOICE_TTS}; they are
 *  module-private so the derived clamp below cannot be spelled from one of them and the group from the other. */
const TTS_ESTIMATE_MS_PER_CHARACTER = 75;
const TTS_WORST_CASE_MS_PER_CHARACTER = 90;

/**
 * The playback-duration model the dead-air ceiling is anchored to: how long assistant text takes to reach the caller's
 * ear, and how far ahead of NOW the resulting cursor is allowed to sit.
 *
 * IT EXISTS BECAUSE THE DO CANNOT OTHERWISE KNOW WHEN THE ASSISTANT STOPPED TALKING, AND ASSUMING IT KNEW IS THE
 * 2026-08-15 INCIDENT. `RELAY_INBOUND_TYPES` carries no playback-completion frame, so the only instant the DO can
 * observe is its own `send` -- and the whole greeting plus the whole opener are still queued in Twilio's TTS pipeline
 * at that moment. The dead-air ceiling was armed from the send instant, so it expired at or just before the first
 * instant the caller could speak: four consecutive live test calls heard the consent notice and were dropped to
 * voicemail, with every counter, every cap and every log line reporting a healthy call.
 *
 * THE MARGIN IS THE TIMEOUT, NOT THE RATE -- and this replaces the "deliberately slow rate" claim that stood here,
 * which the measurement does not support. MEASURED against a real 195-character spoken opener (35 words, 5.57
 * chars/word -- NOT the 6.0 the old note assumed; the since-deleted consent notice was the original specimen):
 * 75ms/char yields 14,625ms, which is 143.6 wpm, INSIDE the 150-180wpm a neural voice delivers rather than safely
 * below it. The rate is also long on phonation and short on prosody -- a per-character model bills a sentence break at
 * one character (75ms) where a neural voice pauses 300-500ms, and a multi-sentence turn carries roughly a second of
 * pause the estimate does not. Net: near break-even, not a safety margin. What makes the fix safe anyway is that the
 * ceiling fires at `cursor + DEAD_AIR_TIMEOUT_MS`, so the estimate only has to be within 12 seconds of truth. Tune the
 * rate against that tolerance, never against a wpm figure alone.
 *
 * CHARACTERS AND NOT WORDS, because the assistant's text arrives as `CHUNK_FLUSH_MAX_CHARS`-bounded fragments that can
 * split a word across two `/chunk` bodies -- so a per-chunk WORD count double-counts at every boundary, while a
 * character count sums exactly however the stream happens to be cut.
 *
 * ONE VENDOR SIGNAL COULD CALIBRATE THIS FROM LIVE CALLS AND IS DELIBERATELY NOT ADOPTED YET.
 * `interrupt.durationUntilInterruptMs` is documented on every `interrupt` frame (Twilio's websocket-messages reference, checked
 * 2026-08-15: `{"type":"interrupt","utteranceUntilInterrupt":"Life is a complex set of","durationUntilInterruptMs":460}`)
 * and is the only ground-truth playback measurement Twilio publishes. It is neither declared in `RELAY_FRAME_FIELDS`
 * nor read. Adopting it is its OWN change and must start as an observation log, not a tuner: the sample arrives only on
 * BARGE-IN turns and covers only the PREFIX a caller talked over, so it over-samples the phonation-heavy head of a turn
 * and under-samples exactly the sentence-break prosody this rate is short on -- calibrating on it naively would push the
 * rate DOWN, which is the unsafe direction. A rate that re-tunes itself from the frames it gates on is also a feedback
 * loop on the one number {@link VOICE_TTS.MAX_QUEUED_AUDIO_MS} exists to bound.
 */
export const VOICE_TTS = {
	/** Milliseconds of audio ONE character of assistant text becomes. The tuning knob; see the group docstring. */
	SPEECH_MS_PER_CHARACTER: TTS_ESTIMATE_MS_PER_CHARACTER,
	/**
	 * The SLOWEST rate a real TTS voice is assumed to reach, and the only input that turns the estimate's error into a
	 * bound. It is an assumption, not a measurement -- which is why it is named rather than folded into the arithmetic
	 * below: a live reading that contradicts it re-tunes ONE number and every derived bound moves with it.
	 */
	WORST_CASE_MS_PER_CHARACTER: TTS_WORST_CASE_MS_PER_CHARACTER,
	/**
	 * How far ahead of NOW the playback cursor may ever sit. THE OVER-COUNT BOUND, and before it existed there was none
	 * in that direction at all: a TTS-side failure that leaves text queued-but-unspoken keeps the cursor growing per
	 * chunk, and the leg then has no dead-air ceiling until the 900-second wall clock -- which composes with the
	 * deliberate decision not to end a call on a relay `error` frame. Clamped, such a leg regains a ceiling in about 72
	 * seconds instead.
	 *
	 * DERIVED, NOT CHOSEN, from the UNDER-count budget, so one number bounds both directions. A turn is safe while
	 * `realMs - estMs < DEAD_AIR_TIMEOUT_MS`; the per-character drift is
	 * `WORST_CASE_MS_PER_CHARACTER - SPEECH_MS_PER_CHARACTER` (15ms), so the safe turn is
	 * `12000 / 15 = 800 characters` of assistant text -- and this clamp is that turn's estimated duration, 60,000ms.
	 * Inside the safe envelope the clamp therefore never binds; past it the turn was already unsafe AT THE WORST-CASE
	 * RATE. (The audit that prompted this put the ceiling at ~970 characters by taking 16.5% of the ESTIMATE rather than
	 * the real duration; the deficit is `15 * chars`, so the figure is 800.)
	 *
	 * IT CHANGED THE FAILURE ENVELOPE RATHER THAN REMOVING IT, AND THE HONEST ACCOUNTING IS THIS. Measured three ways,
	 * by the character count at which a caller is cut off mid-sentence: unclamped at the nominal 75ms/char, NEVER (the
	 * ceiling always sits one full window past the real audio); unclamped at the worst-case 90ms/char, past 800
	 * characters; CLAMPED at the nominal rate, past 960 -- because a clamped cursor fires the ceiling at a fixed
	 * `now + 72,000ms` however long the turn really is. So at the rate we actually expect, the clamp INTRODUCES a
	 * cut-off where none existed. It is still the right trade: the alternative is a leg with no dead-air ceiling at all
	 * until the 900-second wall clock, and 960 characters is roughly 170 spoken words -- a tenfold violation of the two
	 * short sentences `ASSISTANT_VOICE_PROMPT_OVERLAY` asks for, so the envelope is reached only by a turn that is
	 * already a catastrophic call.
	 *
	 * A `max_output_tokens` CAP WAS CONSIDERED AND DECLINED, 2026-08-15, and the reason is not cost. A hard cap
	 * truncates the model mid-sentence too, so the caller hears the same broken answer and only the party doing the
	 * cutting changes; set low enough to matter it becomes an answer-length product decision taken in the wrong layer,
	 * and set at the real envelope it duplicates a bound that already exists. No such parameter is threaded today --
	 * adding one crosses the DO payload, `/agents/run`, `assistant-agents-run` and `assistant-agent-run` -- which is a
	 * four-hop schema change bought against a tenfold prompt violation. The prompt overlay governs length; this clamp
	 * governs the ceiling; neither is pretending to be the other.
	 *
	 * `CHUNK_FLUSH_MAX_CHARS` BOUNDS A CHUNK, NOT A TURN -- its own docstring says it is not the safety boundary -- so
	 * before this clamp nothing bounded the accumulated turn, and past the ceiling a caller is cut off mid-sentence with
	 * `DEAD_AIR_TIMEOUT_MS` recorded on a leg that had no dead air.
	 */
	MAX_QUEUED_AUDIO_MS:
		Math.floor(VOICE_CAPS.DEAD_AIR_TIMEOUT_MS / (TTS_WORST_CASE_MS_PER_CHARACTER - TTS_ESTIMATE_MS_PER_CHARACTER)) *
		TTS_ESTIMATE_MS_PER_CHARACTER,
	/**
	 * How long AFTER the estimated end of playback a caller `prompt` frame is still eligible for the echo filter
	 * (`ConnectCallDO.handleFinalPrompt` -> `matchVoiceEcho`). Line echo is the assistant's own audio arriving back
	 * through the caller's microphone or the PSTN path, so it can only exist while that audio is playing PLUS the
	 * transport tail: speakerphone reverberation, carrier echo-path delay, and the transcription engine's own
	 * finalization lag all land the frame a beat after the audio stopped -- and `handleInterrupt` truncates the cursor
	 * to NOW on a barge-in, so without a tail the echo of the just-cut sentence would always land "outside" playback.
	 * 2.5s covers those tails; it is deliberately far below `DEAD_AIR_TIMEOUT_MS`, so the window can never eat a real
	 * caller turn that arrives after genuine silence. Time gates only ELIGIBILITY -- the word-run match is what decides.
	 */
	ECHO_GRACE_MS: 2500,
} as const;

/**
 * The echo MATCHER's tuning (`matchVoiceEcho`, `utils/telephony/voice-echo-filter.ts`): how much of the assistant's own
 * current utterance a caller frame must repeat, in-order, before it is treated as line echo rather than speech.
 *
 * The run threshold is words, not characters, because transcription re-punctuates and re-cases freely but keeps word
 * order; five in-order words of the agent's own sentence inside its own playback window is not a coincidence a real
 * caller produces. ONE fuzzy word per run absorbs the single-token substitutions ASR makes on its own TTS input
 * (an edit-distance-1 respelling, or a digit transcribed for a number word); a second divergence means the caller is
 * saying something of their own and the frame falls through to the ordinary turn path.
 */
export const VOICE_ECHO = {
	/** Minimum consecutive in-order matched words before a frame counts as echo. */
	MIN_RUN_WORDS: 5,
	/** Fuzzy (non-exact) tokens tolerated inside one matched run. */
	MAX_FUZZY_WORDS_PER_RUN: 1,
	/** Shortest token edit-distance fuzz applies to -- below this, one edit turns any word into any other. */
	FUZZY_MIN_WORD_LENGTH: 3,
} as const;

/** `<Record maxLength>` on the voicemail arm, in seconds. Kept OUT of {@link VOICE_CAPS}: nothing enforces it in the DO -- Twilio does. */
export const VOICE_VOICEMAIL_MAX_SECONDS = 180;

/** R2 key prefix for archived call recordings. Keyed by CallSid so a deletion request over one call is one prefix scan. */
export const VOICE_RECORDING_R2_PREFIX = 'connect-voice';

/**
 * The `customMetadata` member an archived recording carries its owning group under.
 *
 * SPELLED `group_id`, MATCHING THE COLUMN IT COPIES (`calls.group_id`), because the object and the row hold the same
 * fact and a second spelling would be two names for one scope -- the drift a reader joining bucket to table discovers
 * only by finding nothing. It is a KEY NAME rather than a value, which is why it lives beside the prefix it travels
 * with instead of in the SQL leaf: nothing here is SQL, and the R2 side is where a rename would break.
 *
 * ABSENT MEANS UNSCOPED, never an empty string -- the same vocabulary the nullable column uses. The builder that
 * applies it (`tools/telephony/voice-recording-rest.ts`) carries that argument in full.
 */
export const VOICE_RECORDING_GROUP_METADATA_KEY = 'group_id';

/**
 * KV key the answer path reads BEFORE any routing verdict. Present-and-on means every inbound call answers voicemail
 * with `VOICE_HANDOFF_REASONS.KILL_SWITCH` recorded on the calls row at INSERT, with no relay socket minted and no
 * assistant dispatch -- and a WARN line naming the CallSid, because an armed switch is otherwise indistinguishable
 * from a broken assistant to everyone except the operator who armed it.
 */
export const VOICE_KILL_SWITCH_KV_KEY = 'connect:voice:kill-switch';

/**
 * The two values {@link VOICE_KILL_SWITCH_KV_KEY} may hold. An absent key, and any unrecognised value, read as OFF.
 *
 * STRING VALUES, NEVER A BOOLEAN ON THE WIRE: a writer storing `true` / `false` through `kvPut` while the answer path
 * compares against `ON` is two value domains that can never match, and the desk's Answering switch would write a key
 * nothing reads. A LEGACY boolean entry reads as OFF on BOTH readers (it is neither `'on'` nor a value either side
 * recognises), which loses no behaviour: a boolean entry was never read as a switch.
 *
 * `ON` MEANS THE KILL SWITCH IS ENGAGED -- i.e. the assistant does NOT answer. That is the opposite polarity from the
 * flow's `enabled` member, which states whether ANSWERING is on, so the two are mapped in exactly one place
 * ({@link voiceKillSwitchValueFor}) rather than by a ternary at each writer.
 */
export const VOICE_KILL_SWITCH_VALUES = {
	ON: 'on',
	OFF: 'off',
} as const;

/**
 * @runtime-derived-ssot-helper The stored value for an ANSWERING state -- the one place the desk's polarity (`enabled`
 * = the assistant answers) is mapped onto the key's polarity (`ON` = the assistant is suppressed). A second WRITER
 * inventing a third encoding is the failure this exists to foreclose.
 */
export function voiceKillSwitchValueFor(answeringEnabled: boolean): string {
	return answeringEnabled ? VOICE_KILL_SWITCH_VALUES.OFF : VOICE_KILL_SWITCH_VALUES.ON;
}

/**
 * @runtime-derived-ssot-helper Is the switch ENGAGED, given whatever the key currently holds? The DECODE half of
 * {@link voiceKillSwitchValueFor}: an absent key, a legacy boolean and any unrecognised token all read as
 * not-suppressed, because only the exact `ON` token suppresses.
 *
 * THE DECODE HAD NO HELPER AND WAS SPELLED THREE TIMES IN TWO POLARITIES -- the answer path's `=== ON`
 * (`killSwitchEngaged`, in the voice worker entry then, `flows/voice-call-answer.ts` now), the flow's own `get` arm
 * as `!== ON` (`flows/voice-kill-switch.ts`), and a THIRD copy inside the flow's test suite that claimed to be the answer
 * path's line while being a re-implementation of it. This leaf already owned the encode side; the readers were left to
 * agree by inspection, which is precisely the arrangement that let the writer and the readers disagree about the value
 * domain in the first place and made the whole rail inert.
 */
export function voiceKillSwitchEngaged(storedValue: unknown): boolean {
	return storedValue === VOICE_KILL_SWITCH_VALUES.ON;
}

/**
 * EVERY READING THE DESK CAN HAVE OF THE PHONE-NUMBER POOL -- the closed set `ConnectPoolReading`
 * (`types/connect-ui-projections.ts`) is derived from, rather than a union re-typed at each pin that claims to cover it.
 *
 * IT IS A TUPLE BECAUSE THE UNION WAS HAND-ENUMERATED AT EVERY GUARD AND THE GUARDS WERE THE ONLY DEFENCE. The pool
 * pins listed the members by hand, so a FIFTH member compiled everywhere, kept both of them green, and fell past every
 * arm of the top bar's ladder into the kill switch's own "Answering" -- a desk byte-identical to a verified-wired one
 * over a state nothing had taught it, which is the exact indistinguishability the `failed` member was split out to end.
 * Derived, a new member is a red row at the pins and a compile error at `POOL_VERDICT`
 * (`spa/connect-control/frontend/fleet-status.ts`), the total record the ladder reads the pool through.
 *
 * IT LIVES BESIDE THE KILL SWITCH because the two are the top bar's one question read from two inputs: whether the
 * assistant is picking calls up, and whether any call can arrive to be picked up. `wired` is the voice worker's own
 * full-equality verdict over both webhook targets, relayed; `failed` is a survey that ANSWERED an error, which is not
 * the same not-knowing as a survey that has not answered at all (that one is null, and is deliberately not a member).
 */
export const CONNECT_POOL_READINGS = ['none', 'unwired', 'wired', 'failed'] as const;

/**
 * Caller phrases that escalate DETERMINISTICALLY, with no model in the path. Matched case-insensitively as whole words
 * against a finalized caller utterance -- a substring test would fire "human" inside "humanity" and "operator" inside
 * "cooperator", so the matcher word-bounds each entry.
 */
export const VOICE_ESCALATION_PHRASES = ['agent', 'representative', 'human', 'operator', 'real person'] as const;

/** DTMF digit that escalates immediately -- the one keypress the AI leg honours deterministically.
 *  A `DECLINE_RECORDING: '9'` member lived here while the consent notice advertised it; both went together on
 *  2026-08-21 (operator adjudication: notice removed, recording continues). */
export const VOICE_DTMF = {
	ESCALATE: '0',
} as const;

/** Every HTTP path the voice worker declares. The bare POST catch-all is deliberately absent: it is `/*` by construction. */
export const VOICE_ROUTES = {
	STATUS: '/voice/status',
	ACTION: '/voice/action',
	RECORDING: '/voice/recording',
	/**
	 * `<Record action>` target on the voicemail arm -- the row that ENDS a leg that has just left a message.
	 *
	 * IT IS NOT `RECORDING`, AND MERGING THE TWO WOULD RE-BREAK BOTH. Twilio's `<Record>` reference sends this row
	 * `RecordingUrl`, `RecordingDuration` and `Digits` and NO `RecordingSid`, while `stageVoiceRecording` requires a
	 * `RecordingSid` to key its claim and answers a bare `OK` ack -- so a shared row would ack the recording-complete
	 * POST with a non-TwiML body, which Twilio treats as a document error rather than an instruction to hang up.
	 * `RECORDING` is the `recordingStatusCallback` (a DIFFERENT POST, carrying the sid the archive flow pulls media
	 * with) and stays exactly what it was; both fire on one voicemail, which is why they need two paths.
	 */
	VOICEMAIL_COMPLETE: '/voice/voicemail-complete',
	/**
	 * The answer webhook path `voice-number-configure` WRITES onto a Twilio number's `VoiceUrl`.
	 *
	 * IT IS NOT A ROUTE ROW, and that is not an omission: the worker answers every inbound POST on the path-agnostic
	 * `/*` catch-all precisely so a console-configured path cannot 404 a live call. This const exists because the
	 * moment we set the URL through the API instead of the console, the path stops being Twilio's decision and becomes
	 * ours -- and a number-pool run and a status-callback URL must not each spell it separately.
	 */
	ANSWER: '/voice/answer',
	/** WS upgrade the ConversationRelay dials; the path carries `/<callSid>/<token>`. THE ONLY PUBLIC non-webhook row. */
	RELAY: '/relay',
	/** Observer WS upgrade, matched by ConnectCallDO's OWN router; the path carries `/<callSid>`. NOT a worker route --
	 *  spa/connect dials it on a cross-script CONNECT_CALL_DO binding stub, so it has no public HTTP surface. */
	OBSERVE: '/observe',
	/** Supervisor control POST (takeover / hangup / OTP / escalation ack), matched by ConnectCallDO's OWN router; the
	 *  path carries `/<callSid>`. Same DO-binding-only reach as `OBSERVE`. */
	CONTROL: '/control',
	/**
	 * `<Gather action>` target on the escalation whisper call -- the agent's "press 1 to join". Twilio-signed like every
	 * other URL we mint, and the user call it refers to rides the QUERY STRING, which the signature covers.
	 */
	ESCALATION_JOIN: '/voice/escalation-join',
	/** Assistant-side sentence-chunk sink, reached on the ConnectCallDO stub rather than over HTTP. */
	CHUNK: '/chunk',
} as const;

/**
 * Second half of the `(provider, event_id)` idempotency key, so a status callback, an action callback, a recording
 * callback and an escalation join for the SAME CallSid claim distinct rows instead of collapsing into one.
 *
 * TWO OF THEM ARE PREFIXES, NOT WHOLE KEYS. One CallSid delivers a status callback per `CallStatus`, and TWO
 * recordings on any AI leg that ends at a mailbox -- the REST recording started at media-up plus the `<Record>` one
 * the voicemail arm emits -- so those two sites suffix the kind with the delivery's own discriminator
 * (`status:<CallStatus>`, `recording:<RecordingSid>`). A bare kind there makes the second delivery a loser that is
 * acked and dropped, which for the recording row meant its media was never archived and never deleted from Twilio.
 *
 * THERE IS NO `ANSWER` MEMBER, AND ITS ABSENCE IS THE CONTRACT. The answer row owes Twilio TwiML whatever a claim
 * says, so it can never gate on one; nothing else keys on `answer`; and a claim row with no reader is a D1 write plus
 * a flow dispatch inside the caller's TwiML budget that produces nothing. That row's replay safety is structural
 * instead -- an `OR IGNORE` INSERT on the CallSid PK and a relay token that is a pure function of its inputs.
 */
export const VOICE_EVENT_KINDS = {
	STATUS: 'status',
	ACTION: 'action',
	RECORDING: 'recording',
	ESCALATION_JOIN: 'escalation-join',
} as const;

export type VoiceEventKind = (typeof VOICE_EVENT_KINDS)[keyof typeof VOICE_EVENT_KINDS];

/**
 * @runtime-derived-ssot-helper THE CLAIM KEY one voice webhook delivery spends: `<CallSid>:<kind>`, suffixed with the
 * delivery's own discriminator where the kind is a prefix (`status:<CallStatus>`, `recording:<RecordingSid>` -- the
 * header above). Spelled once, since 2026-09-27, for the three graphs that claim (`voice-call-close-out`,
 * `voice-recording-archive`, `voice-escalation-join`), each of which used to receive the key composed by the entry.
 */
export function voiceEventId(callSid: string, kind: VoiceEventKind, discriminator = ''): string {
	return discriminator === '' ? `${callSid}:${kind}` : `${callSid}:${kind}:${discriminator}`;
}

/**
 * What `voice-webhook-claim` answers about one delivery's claim, and the THREE arms are the whole reason the graph
 * answers a word rather than a boolean (the voice claim-failure suite, 2026-08-15): `claimed` is this delivery's to act
 * on; `lost` is the DESIGNED loser, a genuine Twilio redelivery whose side effect already ran, silent on purpose; and
 * `not-run` is a claim that could not run -- a failed run, an absent echo -- which is a DROPPED side effect, because
 * Twilio redelivers nothing this worker answered, so the row's shape reports it loudly and still answers Twilio.
 */
export const VOICE_CLAIM_VERDICTS = {
	CLAIMED: 'claimed',
	LOST: 'lost',
	NOT_RUN: 'not-run',
} as const;

export type VoiceClaimVerdict = (typeof VOICE_CLAIM_VERDICTS)[keyof typeof VOICE_CLAIM_VERDICTS];

/**
 * Query-string parameters the escalation whisper call's `<Gather action>` URL carries. They ride the URL rather than the
 * body because the whisper leg is a DIFFERENT call with its own CallSid -- nothing in Twilio's POST names the USER
 * call -- and because Twilio's signature covers the full URL including its query, so both values are signed.
 */
export const VOICE_ESCALATION_JOIN_PARAMS = {
	CALL_SID: 'call_sid',
	AGENT_USER_ID: 'agent_user_id',
} as const;

// The accept DIGIT is not spelled here: it is `VOICE_ESCALATION.ACK_DIGIT` (`const/connect-voice-escalation.ts`),
// which is also the digit the spoken whisper and the SMS name. A second copy is a silently divergent page.

/**
 * @runtime-derived-ssot-helper The conference name BOTH legs of a takeover dial into -- the user's `<Connect action>`
 * response and the acking agent's whisper-call response. Derived from the CallSid rather than stored, so the two
 * independently-served TwiML responses cannot name different conferences and strand the caller in an empty room; and
 * per-call by construction, so two concurrent takeovers cannot collide into one.
 */
export function voiceConferenceName(callSid: string): string {
	return `voice-${callSid}`;
}

/**
 * WHICH SIDE of a takeover bridge a `<Conference>` document is being rendered for. Two legs dial the SAME room
 * ({@link voiceConferenceName}) and they owe OPPOSITE lifecycle attributes, so the leg is a closed vocabulary rather
 * than a free string a call site could invent a third member of.
 *
 * IT EXISTS BECAUSE THE TWO LEGS DIFFER ON `startConferenceOnEnter` AND ON NOTHING ELSE THE CALLER CAN HEAR. Twilio's
 * default is `true`, so whichever party arrives FIRST starts the room -- and a caller who arrives first into a started
 * room gets no `<Conference waitUrl>` hold audio at all, so they wait for the agent in silence. The caller leg therefore does NOT
 * start the conference and the AGENT leg does, which is what makes "held with music until a person joins" the
 * behaviour rather than "held in silence". `buildConferenceBridgeTwiml` (`utils/telephony/voice-twiml.ts`) is the one
 * reader.
 */
export const VOICE_CONFERENCE_LEGS = {
	/** The inbound caller: the answer-time HUMAN verdict, and the `<Connect action>` takeover callback. */
	CALLER: 'caller',
	/** The paged agent's whisper call, answering its own `<Gather action>` after they press the join digit. */
	AGENT: 'agent',
} as const;

/**
 * The answer path's routing verdict. `HUMAN` is reachable only when the ConnectInbox roster is non-empty -- and the
 * roster is an EMPTY STUB in this lane, which is not an oversight: the locked constraint is that the AI answers exactly
 * when no human is at the desk, so an empty roster is the correct steady state and `HUMAN` is the arm that lights up
 * when presence lands.
 */
export const VOICE_ROUTE_VERDICTS = {
	AI: 'ai',
	HUMAN: 'human',
	VOICEMAIL: 'voicemail',
} as const satisfies Record<string, VoiceRouteVerdict>;

/** The ONE name ConnectInboxDO is ever addressed by. A second name would mint a second "singleton" holding a second, silently divergent fleet counter. */
export const VOICE_INBOX_SINGLETON_NAME = 'connect-inbox';

/** Routes on the ConnectInboxDO stub. Reached over the DO binding, never over HTTP. */
export const VOICE_INBOX_ROUTES = {
	VERDICT: '/inbox/verdict',
	CALL_START: '/inbox/call-start',
	CALL_END: '/inbox/call-end',
	TURN_OUTCOME: '/inbox/turn-outcome',
	/**
	 * READ the singleton's own fleet state -- the live AI-call count and the ceiling it is measured against.
	 *
	 * IT EXISTS BECAUSE DO STORAGE OUTLIVES EVERY DEPLOY. `activeAiCalls` is incremented at answer and decremented at
	 * end, and a LOST end poke (see `notifyVoiceInbox`, `durable-objects/call-handlers/call-inbox-poke.ts`, whose
	 * absorption arm cannot end a live call) leaves the count permanently high. Once it reaches
	 * `VOICE_CAPS.MAX_CONCURRENT_AI_CALLS` every subsequent call answers
	 * voicemail, forever, and no redeploy clears it -- so the counter needs somewhere to be LOOKED AT before it needs
	 * somewhere to be reset.
	 */
	STATE: '/inbox/state',
	/** ZERO the admission state that cannot repair itself: the live AI-call count AND the breaker's failure run,
	 *  answering the values they held. Separate from `CALL_END` / `TURN_OUTCOME` because those routes adjust by one
	 *  and this one discards accumulated wrong answers. The live-call count is the LATCH -- nothing else lowers it. The
	 *  breaker rides along because it is the fast way back rather than the only one: the half-open window releases a
	 *  held breaker after `VOICE_CIRCUIT_BREAKER_COOLDOWN_MS`, but only by spending the next caller as the probe, and
	 *  only after that whole window (witnessed 2026-08-14: failed turns during the kill-switch-inert era tripped it,
	 *  and every later call answered voicemail straight from the greeting). */
	RESET_ADMISSION: '/inbox/reset-admission',
	// STILL NO escalation-ACK route, and {@link ESCALATION_RAISED} below is not one. Ack STATE has two homes and neither
	// is the singleton: the ladder stops on the call DO's own `escalationDeadlineAt`, which the ack re-arms from the
	// rung's ack window to the caller's HOLD window rather than zeroing (zero is what put the acked caller back on the
	// dead-air ceiling), and the deck reads the `observer_log` ack entry off the calls row -- a singleton stamp would be
	// a third copy with no reader. What the route below carries is a NUDGE and no state at all.
	/**
	 * A caller has asked for a human -- fanned out to every attached desk so the alarm fires on the event rather than on
	 * the next poll.
	 *
	 * IT STORES NOTHING, WHICH IS WHAT SEPARATES IT FROM EVERY OTHER ROUTE ON THIS RECORD. The other five each move a
	 * persisted fleet fact; this one is pure transport -- it reaches the singleton ONLY because the singleton is where
	 * the desk sockets are, and a per-call DO's observer fan-out cannot reach a desk that is not already watching that
	 * specific call. That is the whole gap: escalation is the one event where the desk needs to be told about a call it
	 * has never heard of, which is exactly what a per-CallSid channel structurally cannot do.
	 *
	 * THE CALL DO STILL FANS ITS OWN `ESCALATION` FRAME OUT and that is not a duplicate: the per-call frame carries the
	 * REASON to a pane already open on that call, while this one says only "the call set changed, re-read it" to desks
	 * that may have nothing open at all. See {@link VOICE_DESK_FRAME_TYPES} for why nothing more rides this wire.
	 */
	ESCALATION_RAISED: '/inbox/escalation-raised',
	/**
	 * The DESK WATCH upgrade -- the fleet-wide nudge channel, and the only GET on this record.
	 *
	 * IT IS ON THE SINGLETON RATHER THAN ON `ConnectCallDO` FOR ONE STRUCTURAL REASON: `ConnectCallDO` instances are
	 * keyed BY CallSid, so a desk can only open a socket to a call it can already name -- and the event a desk most
	 * needs is a call it has never heard of ARRIVING. That is unaddressable per-call by construction, which is why the
	 * 2-second `connect-calls-list` poll existed. The singleton is the one instance that already learns of every call
	 * start and end (`CALL_START` / `CALL_END` above), so it is the only place the channel can live.
	 */
	DESK_WATCH: '/inbox/desk-watch',
	/**
	 * Twilio phone-number POOL survey and configuration, plus catalogue search and PURCHASE, dialled by the dashboard's
	 * number picker over the cross-script CONNECT_INBOX_DO binding. One route for all four because they are one
	 * flow's action arms, and the route is action-transparent: the far side's enum decides what is runnable.
	 *
	 * IT LIVES ON THE SINGLETON AND NOT IN `spa/connect` BECAUSE OF WHERE THE CREDENTIAL IS. TWILIO_ACCOUNT_SID and
	 * TWILIO_AUTH_TOKEN are Secrets-Store bindings on the VOICE worker; connect binds neither and must never be given
	 * them for a dashboard read. Running `voice-number-configure` in-process here resolves both handler-side, on the
	 * worker that already owns them, and the dashboard sends only an action and a number.
	 */
	NUMBERS: '/inbox/numbers',
} as const;

/**
 * BODY keys on a ConnectInbox poke, one per route family: the caller identity the two COUNTER routes attribute their
 * adjustment to, and the breaker verdict `TURN_OUTCOME` carries.
 *
 * HOSTED HERE BECAUSE THE PRODUCER AND THE READER ARE DIFFERENT MODULES and were spelling the same two words
 * independently -- `notifyVoiceInbox`'s payload union on one side, the singleton's `extractStringInput` key on the
 * other. A poke sent under a key the reader does not know is absorbed exactly like a lost poke: silently, into a
 * counter no operator can attribute afterwards, which is the failure `VOICE_INBOX_ROUTES.RESET_ADMISSION` exists to
 * repair. The type the producer declares is derived from these members, so the two cannot drift.
 */
export const VOICE_INBOX_POKE_KEYS = {
	CALL_SID: 'call_sid',
	SUCCEEDED: 'succeeded',
} as const;

/**
 * Wire keys on the `VOICE_INBOX_ROUTES.VERDICT` exchange -- the ONE request member the answer path sends, and the four
 * answer members it reads back.
 *
 * A SIBLING RECORD RATHER THAN FOUR MORE MEMBERS ON `VOICE_INBOX_POKE_KEYS`, because a POKE is a fire-and-forget
 * bookkeeping write whose body is never read back and whose response is discarded -- `notifyVoiceInbox` returns `void`
 * and says so. The verdict is a REQUEST/RESPONSE on the ANSWER path, it is the only exchange here with an answer
 * anybody reads, and folding it into a record named for pokes would make that name lie about half its members.
 *
 * IT IS HOSTED HERE FOR THE REASON ITS SIBLING IS: producer and reader are DIFFERENT MODULES, and both sides were
 * hand-spelling the same words. `calls_from_ani_this_hour` was a module-local const in `ConnectInboxDO` under a
 * docstring claiming it was "spelled once" while `inboxRoutingVerdict` (now `flows/voice-call-answer.ts`) wrote the
 * literal into a `JSON.stringify` two files away -- and drift on that ONE key is silent by construction: the reader's `typeof raw === 'number'` guard
 * degrades an unrecognised key to 0, `voiceAdmissionAllowed` then passes forever, and the per-ANI admission ceiling is
 * simply gone with every write still reporting success. The four ANSWER keys carried the same split, with the same
 * silence on the other side: an unrecognised `verdict` reads as unparsable and every caller answers voicemail.
 */
export const VOICE_INBOX_VERDICT_KEYS = {
	/** REQUEST: how many AI calls this ANI has already been answered for inside the rolling hour. */
	CALLS_FROM_ANI_THIS_HOUR: 'calls_from_ani_this_hour',
	/** ANSWER: the `VOICE_ROUTE_VERDICTS` member. Anything else, or absent, is an unparsable answer. */
	VERDICT: 'verdict',
	/** ANSWER: the fleet's live AI-leg count at the moment the verdict was taken. */
	ACTIVE_AI_CALLS: 'active_ai_calls',
	/** ANSWER: the ceiling that count is measured against, so a log line can name both halves. */
	MAX_CONCURRENT: 'max_concurrent',
	/** ANSWER: the `VOICE_HANDOFF_REASONS` member behind a `voicemail` verdict, empty on the arms that denied nothing. */
	DENIAL_REASON: 'denial_reason',
} as const;

/**
 * Consecutive failed assistant turns that trip the breaker to voicemail. CONSECUTIVE, not cumulative: an intermittent
 * failure on a long call is noise, while three in a row is the assistant being down, and only the second should stop
 * new calls from engaging the AI at all.
 */
export const VOICE_CIRCUIT_BREAKER_FAILURES = 3;

/**
 * How long a TRIPPED breaker stays closed before it admits ONE probe call -- the half-open window.
 *
 * WITHOUT IT THE BREAKER IS A LATCH, NOT A BREAKER. `recordTurnOutcome` resets the failure run only on a SUCCEEDED
 * turn, and a tripped breaker routes every call to voicemail BEFORE any turn can run -- so it can never observe the
 * success that would release it, and a three-failure blip (a brief model outage, or any bug that miscounts a failure)
 * silenced the whole fleet until a human POSTed `VOICE_INBOX_ROUTES.RESET_ADMISSION`. After this window the next call
 * is admitted to the AI arm and ITS outcome decides: a success clears the run, a failure re-latches for another window.
 *
 * Five minutes is short enough that a transient outage self-heals well inside one caller's patience, and long enough
 * that a genuinely down assistant is not handed a new caller every few seconds.
 */
export const VOICE_CIRCUIT_BREAKER_COOLDOWN_MS = 300000;

// The `/control/<callSid>` ACTION vocabulary is not spelled here: `const/connect-voice-control.ts` owns it, and it
// carries the OTP and escalation-ack rows this two-member copy silently lacked.

/**
 * Live turn state, fanned out to observers and checkpointed to `ctx.storage`. It is the DO's own state machine, NOT a
 * Twilio vocabulary: `THINKING` covers the window between a finalized caller utterance and the first assistant chunk,
 * which is exactly the interval an operator watching the deck needs to see before deciding to break in.
 */
export const VOICE_TURN_STATES = {
	GREETING: 'greeting',
	LISTENING: 'listening',
	THINKING: 'thinking',
	SPEAKING: 'speaking',
	ENDING: 'ending',
} as const;

/**
 * Frame types the DO pushes to an attached observer socket. Observers are READ-ONLY: no inbound observer frame is honoured.
 *
 * NO `attach` / `detach` MEMBERS, AND THE DELETION IS THE FIX RATHER THAN AN OMISSION. Both were declared here, typed
 * into the observer union, and `attach` was genuinely fanned out -- while the desk's own narrower table
 * (`OBSERVER_FRAME_NARROWERS`, `spa/connect/frontend/voice-observer.ts`) DELIBERATELY dropped the whole frame, because
 * who is watching a call is an audit record and not something the pane renders. A wire member with a producer and no
 * reader is a frame every consumer has to be told to ignore, which is the shape a later reader picks up believing it
 * means something. The two VERBS survive, unchanged in value, as {@link VOICE_OBSERVER_LOG_ACTIONS} -- the audit-log
 * vocabulary they were always doing double duty as.
 */
export const VOICE_OBSERVER_FRAME_TYPES = {
	TRANSCRIPT: 'transcript',
	PARTIAL: 'partial',
	STATE: 'state',
	SIGNAL: 'signal',
	ESCALATION: 'escalation',
	END: 'end',
} as const;

/**
 * The verb an `observer_log` entry is keyed under on the `calls` row -- who attached to a live call and when they left.
 *
 * IT IS A LOG VOCABULARY AND NOT A WIRE ONE, which is exactly what the name now says and what the record it was
 * carved out of did not. These two values were members of {@link VOICE_OBSERVER_FRAME_TYPES}, so a reader could not
 * tell the fanned-out frame kinds from the two that only ever reached `auditObserver`'s `entry` JSON. The VALUES are
 * unchanged on purpose: stored `observer_log` rows carry them, and the desk's `acknowledged` projection selects on the
 * same `type` key, so renaming the strings would orphan every row already written.
 */
export const VOICE_OBSERVER_LOG_ACTIONS = {
	ATTACH: 'attach',
	DETACH: 'detach',
} as const;

/**
 * `ctx.storage` keys ConnectCallDO checkpoints under. Hibernation discards memory with NO close event, so anything not
 * written here is gone on the next wake -- and there is no interval to notice, because an interval would defeat
 * hibernation in the first place.
 */
export const VOICE_DO_STORAGE_KEYS = {
	CHECKPOINT: 'connect-call-checkpoint',
	/** The live Tier-1 challenge: `{ codeHash, expiresAt, attempts }`. SEPARATE from the checkpoint on purpose -- the
	 *  checkpoint is rewritten at every utterance-final by paths that know nothing about a challenge, so folding the
	 *  hash into it would let a concurrent turn write erase a challenge the caller is mid-way through answering. */
	OTP_CHALLENGE: 'connect-call-otp-challenge',
} as const;

/** Wrong-code submissions one challenge tolerates before it is burned. Low by construction: a 6-digit code with a
 *  5-minute life needs an attempt ceiling to stay a credential rather than a puzzle. */
export const VOICE_OTP_MAX_ATTEMPTS = 3;

/** Socket-role tag written with `serializeAttachment`, so a hibernation wake can tell a relay socket from an observer. */
export const VOICE_SOCKET_ROLES = {
	RELAY: 'relay',
	OBSERVER: 'observer',
	/**
	 * A DESK watching the whole fleet, attached to `ConnectInboxDO`. Lives in this record beside the two `ConnectCallDO`
	 * roles because `ctx.acceptWebSocket` tags are the hibernation contract for BOTH classes and a second record would
	 * let one class's tag collide with the other's; the two DOs never share a socket table, so the values only have to
	 * be distinct within this record.
	 */
	DESK: 'desk',
} as const;

/**
 * Frame types the SINGLETON pushes to an attached desk. There is exactly one, and the single member IS the design.
 *
 * IT IS A NUDGE AND CARRIES NO CALL DATA. The desk answers it by invalidating its `connect-calls-list` query and
 * re-reading, so that flow stays the ONE source of truth for what is on the list. Putting rows on this wire would make
 * the socket a second producer of the same facts, free to disagree with the fetch -- and this desk already carries the
 * scar from exactly that shape at a smaller scale: `applyObservedEndings`
 * (`components/connect/conversation-stream.ts`) exists solely to adjudicate between the per-call observer's `end`
 * frame and the poll, because the two disagree about whether a call is over. That is one field on one call. The same
 * bug across the whole list is not a trade worth taking to save a round trip on an event that fires when a phone rings.
 *
 * A SINGLE MEMBER RATHER THAN `call-started` / `call-ended` / `escalation`, because the desk's response to all three is
 * byte-identical: re-read the list. Three words the reader switches on identically are three chances for a producer to
 * send a fourth nobody handles. When a desk needs to act DIFFERENTLY per cause, that is when this record earns a second
 * member -- and not before.
 */
export const VOICE_DESK_FRAME_TYPES = {
	/** The call set changed. Re-read `connect-calls-list`; nothing about WHAT changed rides this frame. */
	REFRESH: 'refresh',
} as const;

/**
 * Redial policy for the desk watch socket.
 *
 * IT IS UNCAPPED, AND THAT IS THE OPPOSITE OF `useCallObserver`'S DELIBERATE NO-RECONNECT RULE -- for a reason that
 * does not carry over. The per-call observer refuses to redial because a socket that dropped because THE CALL ENDED
 * would otherwise be retried forever against a call that no longer exists. The desk channel is scoped to the
 * ConnectInbox singleton, which always exists: every drop here is a transport failure, a worker redeploy or a sleeping
 * laptop, and every one of those is worth redialling. A capped ladder would leave a desk that had been idle overnight
 * permanently on poll-only with nothing on screen saying so.
 *
 * THE CEILING IS WHAT MAKES UNCAPPED AFFORDABLE: a fleet-wide outage settles at one handshake per desk per
 * `MAX_DELAY_MS`, which is cheaper than the 2-second poll this channel replaced even in its worst state.
 */
export const CONNECT_DESK_RECONNECT = {
	BASE_DELAY_MS: 1000,
	MAX_DELAY_MS: 30_000,
} as const;

/**
 * `env` key staging a scrubbed transcript utterance for the persistence flow, mirroring `TWILIO_INBOUND_PAYLOAD_ENV_KEY`.
 * Transcript text NEVER rides flow `input`: the engine publishes a flow's input verbatim onto the run and step spans
 * and onto the local run journal's retained step rows, so an
 * utterance placed there is republished into telemetry no downstream redaction control was asked to cover. `env` reaches
 * step handlers as the third argument and is not serialized into step records.
 */
export const VOICE_TRANSCRIPT_ENV_KEY = 'connectVoiceTranscript';

/** `messages.from_identifier` value for a caller-side voice utterance; the assistant side reuses `MESSAGE_CONSTANTS.IDENTITY_ALEX`. */
export const VOICE_CALLER_IDENTITY = 'voice-caller';

/**
 * `messages.from_identifier` value for a FILTERED ECHO row -- the assistant's own words arriving back on the caller
 * channel and suppressed by the echo filter (`ConnectCallDO.handleFinalPrompt`). The `from_identifier` IS the persisted
 * flag: the transcript role rides the wire but is never stored, so this value is what lets the deck (and any later
 * read) tell filtered echo from caller speech on the row itself. It must stay distinct from `VOICE_CALLER_IDENTITY` --
 * collapsing the two would re-label the assistant's own sentence as something the caller said, which is exactly the
 * misattribution the filter exists to stop.
 */
export const VOICE_ECHO_IDENTITY = 'voice-echo';

/**
 * `env` key staging the calls-row INSERT payload. It rides `env` for the same reason the transcript does: the payload
 * carries the caller's phone number on BOTH sides of the call, and flow `input` is published verbatim onto the run and
 * step spans and onto the local run journal's retained step rows.
 */
export const VOICE_CALL_PAYLOAD_ENV_KEY = 'connectVoiceCallRow';

/** Transcript roles, as fanned out on the observer socket and mapped onto `messages.from_identifier` by
 *  `identityForRole` (`utils/telephony/voice-transcript-wire-message.ts`). The role itself is NOT persisted -- the
 *  identity is the stored carrier -- which is why ECHO needs its own identity const above. */
export const VOICE_TRANSCRIPT_ROLES = {
	CALLER: 'caller',
	ASSISTANT: 'assistant',
	/** The assistant's own words echoed back on the caller channel and suppressed by the echo filter: recorded so the
	 *  transcript stays complete, never dispatched, never charged against the turn cap. */
	ECHO: 'echo',
} as const;

// VOICE WEBHOOK ROWS

/** The output every voice webhook ROW answers: a detail flow's `success` and `data`, plus the claim members the row's
 *  unclaimed and not-ended terminals bind -- `claimed`, the three-way `claim` word (`VOICE_CLAIM_VERDICTS`), the
 *  `event_id` the claim was keyed on, and `closed` for a status row that spent its claim on a call still up
 *  (QUAL-FLOW-OUTPUTSCHEMA-TERMINAL-COVERAGE-001, 2026-09-27). No `required`: the unclaimed arm binds no `data` of the
 *  row's own, the same family invariant SUCCESS_DATA_OUTPUT_SCHEMAS states. */
export const VOICE_WEBHOOK_ROW_OUTPUT_SCHEMA = {
	properties: {
		success: { type: 'boolean' },
		data: { type: 'object' },
		claimed: { type: 'boolean' },
		claim: { type: 'string' },
		event_id: { type: 'string' },
		closed: { type: 'boolean' },
	},
} satisfies FlowInputSchema;
